/**
 * DatasetsInterface Component
 *
 * Main container for the datasets list view. Fetches datasets from the API,
 * provides filtering and search capabilities, and renders the datasets table.
 *
 * Reuses shared components (SearchButton, TableFiltersSelector) following the
 * same patterns established in the survey catalogue.
 */

import { useEffect, useState, useCallback, useMemo } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Plus } from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import {
  Dataset,
  getDatasets,
  getDatasetMetadataDomains,
  getPermittedUsersOptions,
  isSharedWithUser,
  UserOption,
} from "@/services/datasetsService"
import SearchButton from "@/components/shared/SearchButton"
import TableFiltersSelector, { FilterOption } from "@/components/shared/TableFiltersSelector"
import DatasetsTable from "@/components/datasets/DatasetsTable"
import DatasetDetailsPanel from "@/components/datasets/DatasetDetailsPanel"
import LoadingSpinner from "@/components/shared/LoadingSpinner"

/**
 * Build filter options from the loaded datasets and metadata domains.
 *
 * Creates filterable options for: Data Topic, Unit of Analysis, Countries,
 * Temporal Resolution, Spatial Resolution, Owner, Shared With Me, and Biblio ID.
 *
 * @param datasets - The list of all datasets.
 * @param users - The list of users (for owner display names).
 * @param countryNameMap - ISO3 → human-readable country name lookup, used so
 *     the Countries filter dropdown shows names rather than ISO3 codes.
 * @param currentUserSub - The current user's Auth0 'sub' claim.
 * @returns Array of filter option definitions for TableFiltersSelector.
 */
function buildFilterOptions(
  datasets: Dataset[],
  users: UserOption[],
  countryNameMap: Map<string, string>,
  currentUserSub: string
): FilterOption[] {
  // Build a map of user sub -> display name for owner labels
  const userNameMap = new Map<string, string>(users.map((u) => [u.sub, u.name]))

  // Extract unique values from datasets for each filterable field
  const dataTopics = new Set<string>()
  const unitsOfAnalysis = new Set<string>()
  const countries = new Set<string>()
  const temporalResolutions = new Set<string>()
  const spatialAggregationLevels = new Set<string>()
  const owners = new Set<string>()
  const biblioIds = new Set<string>()

  for (const dataset of datasets) {
    dataset.dataTopic.forEach((dt) => dataTopics.add(dt))
    dataset.unitOfAnalysis?.forEach((u) => unitsOfAnalysis.add(u))
    dataset.countries?.forEach((c) => countries.add(c))
    dataset.temporalResolution?.forEach((tr) => temporalResolutions.add(tr))
    dataset.spatialAggregationLevel?.forEach((sal) => spatialAggregationLevels.add(sal))
    owners.add(dataset.owner)
    if (dataset.biblioId !== undefined) {
      biblioIds.add(String(dataset.biblioId))
    }
  }

  // Check if any dataset is shared with the current user (not owned by them)
  const hasSharedDatasets = datasets.some(
    (d) => d.owner !== currentUserSub && isSharedWithUser(d, currentUserSub)
  )

  return [
    {
      key: "dataTopic",
      label: "Data Topic",
      options: [...dataTopics].sort(),
    },
    {
      key: "unitOfAnalysis",
      label: "Unit of Analysis",
      // Includes both seed values and any user-supplied "Other" values that
      // have been saved to existing datasets — they're indistinguishable in
      // the stored list, so the filter shows them all together.
      options: [...unitsOfAnalysis].sort(),
    },
    {
      key: "countries",
      label: "Countries",
      // Display country names rather than the ISO3 codes stored on the
      // datasets. ISO3 codes that have no entry in the domains lookup fall
      // back to the raw ISO3 (mirrors the Owner-name-or-sub fallback below).
      options: [...countries]
        .map((iso3) => countryNameMap.get(iso3) ?? iso3)
        .sort(),
    },
    {
      key: "temporalResolution",
      label: "Temporal Resolution",
      options: [...temporalResolutions].sort(),
    },
    {
      key: "spatialAggregationLevel",
      label: "Level of Spatial Aggregation",
      options: [...spatialAggregationLevels].sort(),
    },
    {
      key: "owner",
      label: "Owner",
      // Display owner names, falling back to the sub if name not found
      options: [...owners].map((sub) => userNameMap.get(sub) ?? sub).sort(),
    },
    ...(hasSharedDatasets
      ? [
          {
            key: "sharedWithMe",
            label: "Shared With Me",
            options: ["Yes", "No"],
          },
        ]
      : []),
    ...(biblioIds.size > 0
      ? [
          {
            key: "biblioId",
            label: "Biblio ID",
            options: [...biblioIds].sort((a, b) => Number(a) - Number(b)),
          },
        ]
      : []),
  ]
}

const PAGE_TITLE = "Datasets"

export default function DatasetsInterface(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const { getAccessToken, user } = useAuth()

  // Data state
  const [datasets, setDatasets] = useState<Dataset[]>([])
  const [users, setUsers] = useState<UserOption[]>([])
  // ISO3 → country-name lookup, used to display country names in the
  // Countries filter dropdown while datasets continue to store ISO3 codes.
  const [countryNameMap, setCountryNameMap] = useState<Map<string, string>>(
    () => new Map()
  )
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Filter/search state — initialized from URL params
  const [searchQuery, setSearchQuery] = useState<string>(
    () => searchParams.get("search") ?? ""
  )
  const [activeFilters, setActiveFilters] = useState<Map<string, Set<string>>>(() => {
    const filters = new Map<string, Set<string>>()
    searchParams.forEach((value, key) => {
      if (key.startsWith("filter.")) {
        const filterKey = key.slice(7)
        filters.set(filterKey, new Set(value.split(",")))
      }
    })
    return filters
  })

  // Current user's Auth0 'sub' claim for ownership/sharing logic
  const currentUserSub: string = user?.sub ?? ""

  // Selected dataset ID from URL query param
  const selectedDatasetId: string | null = searchParams.get("datasetId")

  // Derive selected dataset from URL param (after datasets are loaded)
  const selectedDataset = useMemo<Dataset | null>(() => {
    if (!selectedDatasetId) return null
    return datasets.find((d) => d.datasetId === selectedDatasetId) ?? null
  }, [selectedDatasetId, datasets])

  /**
   * Handle dataset selection — update the URL query param.
   *
   * @param dataset - The selected dataset.
   */
  const handleDatasetSelect = useCallback(
    (dataset: Dataset): void => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set("datasetId", dataset.datasetId)
        return next
      })
    },
    [setSearchParams]
  )

  /**
   * Handle closing the details panel — remove the datasetId param.
   */
  const handleClosePanel = useCallback((): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete("datasetId")
      return next
    })
  }, [setSearchParams])

  // Build a map of user sub -> display name for rendering
  const userNameMap = useMemo<Map<string, string>>(
    () => new Map(users.map((u) => [u.sub, u.name])),
    [users]
  )

  // Build a map of user sub -> is_active for rendering removed-user indicators.
  // Defaults to true for legacy records that pre-date the is_active field.
  const userActiveMap = useMemo<Map<string, boolean>>(
    () => new Map(users.map((u) => [u.sub, u.is_active !== false])),
    [users]
  )

  // Sync state changes back to URL (preserving the datasetId param if present)
  useEffect(() => {
    const params: Record<string, string> = {}

    if (searchQuery) {
      params.search = searchQuery
    }

    activeFilters.forEach((values, key) => {
      if (values.size > 0) {
        params[`filter.${key}`] = [...values].join(",")
      }
    })

    // Preserve the datasetId query param if a dataset is selected
    if (selectedDatasetId) {
      params.datasetId = selectedDatasetId
    }

    setSearchParams(params, { replace: true })
  }, [searchQuery, activeFilters, selectedDatasetId, setSearchParams])

  /**
   * Fetch datasets and supporting data (users for owner names, country
   * domains for ISO3 → name lookup).
   */
  const fetchData = useCallback(async (): Promise<void> => {
    try {
      setLoading(true)
      const token = await getAccessToken()

      // Fetch datasets, users, and country domains in parallel.
      const [datasetsData, usersData, domainsData] = await Promise.all([
        getDatasets(token),
        getPermittedUsersOptions(token, false, true),
        getDatasetMetadataDomains(token),
      ])

      setDatasets(datasetsData)
      setUsers(usersData)
      // Build the ISO3 → name lookup once; reused for filter labels and
      // for the country filter matching below.
      setCountryNameMap(
        new Map(domainsData.countries.map((c) => [c.iso3, c.countryName]))
      )
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load datasets")
    } finally {
      setLoading(false)
    }
  }, [getAccessToken])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Build filter options from the loaded data
  const filterOptions = useMemo<FilterOption[]>(
    () => buildFilterOptions(datasets, users, countryNameMap, currentUserSub),
    [datasets, users, countryNameMap, currentUserSub]
  )

  // Apply filters and search to produce the displayed datasets
  const filteredDatasets = useMemo<Dataset[]>(() => {
    let result = datasets

    // Apply active filters
    activeFilters.forEach((selectedValues, filterKey) => {
      if (selectedValues.size === 0) return

      result = result.filter((dataset) => {
        switch (filterKey) {
          case "dataTopic":
            // Match if any of the dataset's topics are selected
            return dataset.dataTopic.some((dt) => selectedValues.has(dt))

          case "unitOfAnalysis":
            // Match if any of the dataset's units of analysis are selected.
            // Datasets without the field are excluded (use ?? false).
            return dataset.unitOfAnalysis?.some((u) => selectedValues.has(u)) ?? false

          case "countries":
            // Filter values are country names (per buildFilterOptions); the
            // dataset stores ISO3 codes, so map each ISO3 through the lookup
            // before testing membership. Same iso3-or-fallback rule used in
            // the filter labels.
            return (
              dataset.countries?.some((iso3) => {
                const name = countryNameMap.get(iso3) ?? iso3
                return selectedValues.has(name)
              }) ?? false
            )

          case "temporalResolution":
            return dataset.temporalResolution?.some((tr) => selectedValues.has(tr)) ?? false

          case "spatialAggregationLevel":
            return dataset.spatialAggregationLevel?.some((sal) => selectedValues.has(sal)) ?? false

          case "owner":
            // Filter by owner display name
            const ownerName = userNameMap.get(dataset.owner) ?? dataset.owner
            return selectedValues.has(ownerName)

          case "sharedWithMe":
            const isSharedWithMe =
              dataset.owner !== currentUserSub &&
              isSharedWithUser(dataset, currentUserSub)
            if (selectedValues.has("Yes") && !selectedValues.has("No")) {
              return isSharedWithMe
            }
            if (selectedValues.has("No") && !selectedValues.has("Yes")) {
              return !isSharedWithMe
            }
            return true

          case "biblioId":
            return dataset.biblioId !== undefined && selectedValues.has(String(dataset.biblioId))

          default:
            return true
        }
      })
    })

    // Apply text search (searches title, description, countries, data topics, units of analysis)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      result = result.filter((dataset) => {
        const searchableFields = [
          dataset.title,
          dataset.description,
          ...(dataset.countries ?? []),
          ...dataset.dataTopic,
          ...(dataset.unitOfAnalysis ?? []),
          dataset.biblioId !== undefined ? String(dataset.biblioId) : "",
        ]
        return searchableFields.some((field) => field?.toLowerCase().includes(query))
      })
    }

    return result
  }, [datasets, activeFilters, searchQuery, userNameMap, countryNameMap, currentUserSub])

  // Loading state
  if (loading) {
    return <LoadingSpinner loadingContent="Loading datasets..." />
  }

  return (
    <>
    <title>{PAGE_TITLE} - MAP Apps Platform</title>
    <div className="flex flex-1 min-h-0">
      {/* Main content — table card */}
      <Card className="flex-1 min-w-0 flex flex-col min-h-0 shadow-none">
        <CardHeader className="pb-0">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>
                <h1>{PAGE_TITLE}</h1>
              </CardTitle>
              <CardDescription>Browse and add new datasets</CardDescription>
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to="/datasets/new">
                <Plus data-icon="inline-start" />
                New Dataset
              </Link>
            </Button>
          </div>
        </CardHeader>

        <CardContent className="flex flex-1 flex-col min-h-0">
          {/* Error banner */}
          {error && (
            <div className="mb-4 p-3 border border-destructive/50 bg-destructive/10 rounded-md">
              <p className="text-sm text-destructive">
                <span className="font-medium">Error loading datasets:</span> {error}
              </p>
            </div>
          )}

          {/* Filter controls row */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="text-sm text-muted-foreground">
              {error
                ? "Unable to load datasets"
                : filteredDatasets.length === datasets.length
                  ? `${datasets.length} datasets`
                  : `${filteredDatasets.length} of ${datasets.length} datasets`}
            </div>
            <div className="flex items-center gap-4">
              <SearchButton
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search title, description, countries…"
                disabled={!!error}
              />
              <TableFiltersSelector
                filterOptions={filterOptions}
                onSave={setActiveFilters}
                initialFilters={activeFilters}
                disabled={!!error}
              />
            </div>
          </div>

          {/* Datasets table */}
          <DatasetsTable
            datasets={filteredDatasets}
            userNameMap={userNameMap}
            currentUserSub={currentUserSub}
            selectedDatasetId={selectedDataset?.datasetId}
            onDatasetSelect={handleDatasetSelect}
          />
        </CardContent>
      </Card>

      {/* Inline details panel — appears on the right when a dataset is selected */}
      {selectedDataset && (
        <DatasetDetailsPanel
          // Key by datasetId so React remounts the panel when the selected
          // dataset changes. Without this, transient local state (notably the
          // selected version) would leak across datasets, making a version
          // picked for one dataset incorrectly persist for another.
          key={selectedDataset.datasetId}
          dataset={selectedDataset}
          userNameMap={userNameMap}
          userActiveMap={userActiveMap}
          currentUserSub={currentUserSub}
          onClose={handleClosePanel}
          onNoteAdded={fetchData}
        />
      )}
    </div>
    </>
  )
}
